import { useEffect, useState } from "react";

import { odswiezPasek } from "../lib/marka";

/**
 * Przełącznik motywu dla procesu kandydata.
 *
 * DESIGN.md przez długi czas trzymał proces wyłącznie w jasnym motywie —
 * uzasadnienie było dobre: ekran bywa oglądany w słońcu na parkingu, drukowany
 * i zapisywany do PDF-u. Ciemny wariant jest teraz **wyborem**, nie domyślną
 * odpowiedzią na ustawienie systemu: nikt nie zostaje wrzucony w ciemny motyw
 * bez własnej decyzji, a druk i PDF dalej wychodzą z jasnego, bo przeglądarka
 * drukuje to, co widzi, a domyślnie widzi papier.
 *
 * Klucz jest inny niż w panelu (`panel-motyw`): konsultant i kandydat to dwa
 * różne konteksty na tym samym urządzeniu — konsultant może chcieć ciemny panel
 * w biurze i jasny podgląd procesu, który za chwilę pokaże człowiekowi.
 */

const KLUCZ = "motyw";

export type Motyw = "light" | "dark";

function odczytaj(): Motyw {
  try {
    return window.localStorage.getItem(KLUCZ) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

export function useMotyw(): [Motyw, (next: Motyw) => void] {
  // Atrybut jest już na <html> — kładzie go skrypt z index.html przed pierwszym
  // malowaniem. Tutaj tylko synchronizujemy z nim stan Reacta.
  const [motyw, setMotyw] = useState<Motyw>(odczytaj);

  useEffect(() => {
    const root = document.documentElement;
    if (motyw === "dark") root.setAttribute("data-theme", "dark");
    else root.removeAttribute("data-theme");

    // Kolor paska przeglądarki idzie za płótnem — czytany z tokenów, bo marka
    // ma własne płótno, a stała wartość dawałaby szew nad stroną.
    odswiezPasek();

    try {
      window.localStorage.setItem(KLUCZ, motyw);
    } catch {
      /* prywatne okno — motyw nie przeżyje odświeżenia i tyle */
    }
  }, [motyw]);

  return [motyw, setMotyw];
}

/**
 * Dwie opcje obok siebie zamiast jednego przycisku-przełącznika: przy dwóch
 * stanach widać, który jest wybrany, bez czytania. Pojedynczy przycisk zawsze
 * zmusza do zgadywania, czy ikona pokazuje stan bieżący, czy ten po kliknięciu.
 */
export function MotywSwitch({
  motyw,
  onMotyw,
  etykiety,
}: {
  motyw: Motyw;
  onMotyw: (next: Motyw) => void;
  etykiety: { jasny: string; ciemny: string };
}) {
  return (
    <div className="motyw" role="group" aria-label={etykiety.jasny + " / " + etykiety.ciemny}>
      <button
        type="button"
        className={`motyw__opt${motyw === "light" ? " motyw__opt--on" : ""}`}
        aria-pressed={motyw === "light"}
        aria-label={etykiety.jasny}
        title={etykiety.jasny}
        onClick={() => onMotyw("light")}
      >
        <Slonce />
      </button>
      <button
        type="button"
        className={`motyw__opt${motyw === "dark" ? " motyw__opt--on" : ""}`}
        aria-pressed={motyw === "dark"}
        aria-label={etykiety.ciemny}
        title={etykiety.ciemny}
        onClick={() => onMotyw("dark")}
      >
        <Ksiezyc />
      </button>
    </div>
  );
}

function Slonce() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <circle cx="12" cy="12" r="4.2" />
        <path d="M12 2.6v2.2M12 19.2v2.2M2.6 12h2.2M19.2 12h2.2M5.4 5.4l1.6 1.6M17 17l1.6 1.6M18.6 5.4 17 7M7 17l-1.6 1.6" />
      </g>
    </svg>
  );
}

function Ksiezyc() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M20 14.2A8.2 8.2 0 0 1 9.8 4a8.2 8.2 0 1 0 10.2 10.2Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}
